import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Skeleton,
  Stack,
  TextField,
} from '@customink/pigment-react';
import { TAX_EXEMPTION_STATUSES } from '@customink/project-service-types';
import CircularProgress from '@mui/material/CircularProgress';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { AfterVerifiedState } from './AfterVerifiedState';
import { BusinessAddressForm } from './BusinessAddressForm';
import { ChatWithUs } from './ChatWithUS';
import { InfoText } from './InfoText';
import type { BusinessAddressFormData } from './types';
import { avalaraTakingTooLongMessage, usStatesAndTerritories } from './util';

import TaxExemptNotApproved from '@/components/Alerts/TaxExemptNotApproved';
import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';
import { useCreateOrUpdateCustomer } from '@/hooks/Accounts/TaxExemptionECM/useCreateOrUpdateCustomer';
import { useGetCustomer } from '@/hooks/Accounts/TaxExemptionECM/useGetCustomer';
import { useGetOrCreatePortalLink } from '@/hooks/Accounts/TaxExemptionECM/useGetOrCreatePortalLink';
import { useUpdateProjectConfigTaxExemption } from '@/hooks/Project';

interface ApplyTaxExemptionDialogProps {
  open: boolean;
  onClose: (hasVerified?: boolean) => void;
  state: string;
}

export function ApplyTaxExemptionDialogECM({
  open,
  onClose,
  state,
}: ApplyTaxExemptionDialogProps) {
  const [updateProjectConfigTaxExemption] =
    useUpdateProjectConfigTaxExemption();
  const customer = useGetCustomer();
  const customerHasBusinessAddress =
    customer.isLoading || customer.isError
      ? undefined
      : Boolean(customer.data?.businessAddress);
  const createOrUpdateCustomer = useCreateOrUpdateCustomer();

  const [getOrCreatePortalLink, portalLink] = useGetOrCreatePortalLink();
  const [verifyButtonTouched, setVerifyButtonTouched] = useState(false);
  const verifyButtonLoading =
    verifyButtonTouched &&
    (portalLink.isLoading || createOrUpdateCustomer.isLoading);
  const continueButtonLoading =
    !verifyButtonTouched && createOrUpdateCustomer.isLoading;
  const portalLinkInProgress = portalLink.data?.status === 'InProgress';

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (!open) {
      // we want to postpone resetting it to false until the dialog closing animation is finished
      timeoutId = setTimeout(() => {
        setVerifyButtonTouched(false);
      }, 200);
    }
    return () => clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (customerHasBusinessAddress) {
      getOrCreatePortalLink();
    }
  }, [getOrCreatePortalLink, customerHasBusinessAddress]);

  useEffect(() => {
    if (customer.isError || createOrUpdateCustomer.isError) {
      googleAnalyticsTag({
        event: 'customEvent',
        eventCategory: 'Tax Exempt Modal',
        eventAction: 'Error',
        eventLabel: 'Failed Verification',
      });
    }
  }, [customer.isError, createOrUpdateCustomer.isError]);

  const { register, handleSubmit, formState, control, watch } =
    useForm<BusinessAddressFormData>({
      defaultValues: { organizationName: customer.data?.name },
    });

  const handleVerify = useCallback(
    async (data: BusinessAddressFormData) => {
      setVerifyButtonTouched(true);
      if (customerHasBusinessAddress) {
        if (portalLink.data?.status === 'Ready') {
          window.open(portalLink.data.invitation.requestLink, '_blank');
        }
        // else nothing, ui handles that
      } else {
        await createOrUpdateCustomer.trigger(data);
        // portal link request will only succeed if the business address is present
        const tePortalWindow = window.open(
          '/account/redirect/tax-exemption-portal',
          '_blank',
        );
        const portalLinkResult = await getOrCreatePortalLink();
        if (portalLinkResult.data.status === 'Ready') {
          if (tePortalWindow) {
            tePortalWindow.location.href =
              portalLinkResult.data.invitation.requestLink;
          } else {
            console.warn(
              'Browser blocked opening a new tab. Redirecting in this tab as a fallback.',
            );
            window.location.href = portalLink.data.invitation.requestLink;
          }
        } else {
          tePortalWindow?.close();
          // Case when `status !== 'Ready'` handled declaratively below
        }
      }
    },
    [
      createOrUpdateCustomer.trigger,
      getOrCreatePortalLink,
      onClose,
      customerHasBusinessAddress,
      portalLink.data,
    ],
  );

  const handleContinue = useCallback(
    async (data: BusinessAddressFormData) => {
      if (!customerHasBusinessAddress) {
        const result = await createOrUpdateCustomer.trigger(data);
        if (!result?.error) {
          updateProjectConfigTaxExemption(TAX_EXEMPTION_STATUSES.CLAIMED);
          onClose();
        }
        // else nothing, ui handles that
      } else {
        updateProjectConfigTaxExemption(TAX_EXEMPTION_STATUSES.CLAIMED);
        onClose();
      }
    },
    [createOrUpdateCustomer.trigger, customerHasBusinessAddress],
  );

  const handleDialogClose = () => {
    googleAnalyticsTag({
      event: 'customEvent',
      eventCategory: 'Tax Exempt Modal',
      eventAction: 'Click',
      eventLabel: 'Close',
    });
    onClose(verifyButtonTouched);
  };

  return (
    <Dialog
      data-testid="apply-tax-exemption-dialog"
      disableRestoreFocus
      onClose={handleDialogClose}
      open={open}
      title={
        <span data-testid="tax-exemption-dialog-header">
          {verifyButtonTouched ? 'Next Steps' : 'Verify Tax Exempt Status'}
        </span>
      }
      width="lg"
    >
      <DialogContent>
        {verifyButtonTouched ? (
          <AfterVerifiedState />
        ) : (
          <>
            <TaxExemptNotApproved
              taxState={usStatesAndTerritories[state] ?? state}
            />
            <InfoText
              showMigrationNotice={customerHasBusinessAddress === false}
            />
            {customer.isError && (
              <Alert data-testid="tax-exempt-load-error" severity="error">
                We were unable to load your tax exemption information. Please
                try again later.
              </Alert>
            )}
            {createOrUpdateCustomer.isError && (
              <Alert data-testid="tax-exempt-save-error" severity="error">
                We were unable to save your tax exemption information. Please
                try again later.
              </Alert>
            )}
            {customer.isLoading && (
              <Skeleton width="100%">
                <TextField />
              </Skeleton>
            )}
            <form
              autoComplete="organization"
              data-testid="tax-exemptions-form"
              id="tax-exemptions-form"
            >
              {customerHasBusinessAddress === false && (
                <BusinessAddressForm
                  control={control}
                  formState={formState}
                  register={register}
                  watch={watch}
                />
              )}
              <Box pt={2}>
                <FormControl error={Boolean(formState.errors.confirmation)}>
                  <FormHelperText autoFocus>
                    {formState.errors.confirmation?.message}
                  </FormHelperText>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name="confirmation"
                        render={({ field: { onChange, value = false } }) => (
                          <Checkbox
                            checked={value}
                            data-testid="tax-exempt-eligible-confirmation-checkbox"
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        )}
                        rules={{
                          required:
                            'Please confirm that this purchase is eligible for tax exemption.',
                        }}
                      />
                    }
                    label="I confirm this purchase is eligible for tax exemption"
                  />
                </FormControl>
              </Box>
            </form>
            {portalLinkInProgress && (
              <Alert severity="info">{avalaraTakingTooLongMessage}</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Stack
          gap={1}
          justifyContent="flex-end"
          sx={{
            flexDirection: { sm: 'column-reverse', md: 'row' },
            justifyContent: 'center',
            alignItems: { sm: 'stretch', md: 'end' },
            width: '100%',
          }}
        >
          <ChatWithUs />
          {verifyButtonTouched ? (
            <Button
              data-testid="tax-exempt-continue-button"
              onClick={() => onClose(true)}
              sx={{ minWidth: 150 }}
              type="submit"
            >
              Continue
            </Button>
          ) : (
            <>
              <Button
                data-testid="tax-exempt-verify-now-button"
                disabled={
                  customer.isLoading ||
                  customer.isError ||
                  portalLinkInProgress ||
                  verifyButtonLoading
                }
                onClick={handleSubmit(handleVerify)}
                sx={{ minWidth: 130 }}
                variant="secondary"
              >
                {verifyButtonLoading ? (
                  <CircularProgress color="secondary" size={23} />
                ) : (
                  'Verify Now'
                )}
              </Button>
              <Button
                data-testid="tax-exempt-continue-with-purchase-button"
                disabled={
                  customer.isLoading ||
                  customer.isError ||
                  continueButtonLoading
                }
                form="tax-exemptions-form"
                onClick={handleSubmit(handleContinue)}
                sx={{ minWidth: 210 }}
                type="submit"
              >
                {continueButtonLoading ? (
                  <CircularProgress color="secondary" size={23} />
                ) : (
                  'Continue With Purchase'
                )}
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
